import { createContext, useCallback, useContext, useRef, useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useTranslation } from "react-i18next";

type ConfirmOptions = {
  title?: string;
  message: string;
  /** "Confirmer" by default — pass a verb that matches the action. */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Danger styling for destructive actions. */
  danger?: boolean;
};

type Ctx = {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
};

const ConfirmCtx = createContext<Ctx | null>(null);

/**
 * Drop-in replacement for ``window.confirm`` that opens a MUI Dialog and
 * resolves a Promise<boolean>. Provide ``<ConfirmProvider>`` once at the
 * top of the app tree and call ``useConfirm()`` anywhere below.
 */
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback((o: ConfirmOptions) => {
    setOpts(o);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const handleClose = (value: boolean) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setOpts(null);
  };

  return (
    <ConfirmCtx.Provider value={{ confirm }}>
      {children}
      <Dialog
        open={opts !== null}
        onClose={() => handleClose(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {opts?.title ?? t("common.confirmTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: "pre-line" }}>
            {opts?.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => handleClose(false)}>
            {opts?.cancelLabel ?? t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            color={opts?.danger ? "error" : "primary"}
            onClick={() => handleClose(true)}
            autoFocus
          >
            {opts?.confirmLabel ?? t("common.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmCtx.Provider>
  );
}

export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx.confirm;
}
